/**
 * Collection discovery — Admin SDK only via Cloud Function.
 *
 * The Firestore Web SDK cannot list root collections. Discovery MUST run through
 * the `listFirestoreCollections` callable (Firebase Admin SDK).
 */
import { collection, getDocs, limit, query } from "firebase/firestore";
import {
  getActiveRuntimeConnectionId,
  getRuntimeDb,
  hasActiveRuntime,
} from "../features/firestoreStudio/runtime/runtimeDb";
import { listFirestoreCollections } from "./adminApi";

export const REGISTRY_COLLECTION = "firestore_collection_registry";
export const DISCOVERY_ENGINE = "cloud_function";
export const COLLECTION_DISCOVERY_MIN_VERSION = "2.1.0";
export const COLLECTION_REGISTRY_SYNC_MIN_VERSION = "2.3.0";
export const WEB_SDK_LIST_COLLECTIONS_ERROR =
  "Collection discovery cannot run in the Web SDK. Firestore Web SDK does not support listCollections() at the database root. Use the listFirestoreCollections Cloud Function (Admin SDK).";

const normalizeName = (name) => String(name || "").trim();

function parseVersionParts(version) {
  return String(version || "")
    .split(".")
    .map((n) => Number.parseInt(n, 10) || 0);
}

function isVersionAtLeast(version, minimum) {
  const a = parseVersionParts(version);
  const b = parseVersionParts(minimum);
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const av = a[i] || 0;
    const bv = b[i] || 0;
    if (av > bv) return true;
    if (av < bv) return false;
  }
  return true;
}

/**
 * Guard — never call Firestore.listCollections() from the browser.
 */
export function assertNotFrontendListCollections(context = "") {
  throw new Error(
    WEB_SDK_LIST_COLLECTIONS_ERROR + (context ? ` (${context})` : "")
  );
}

/**
 * @deprecated Intentionally throws — Web SDK cannot list root collections.
 */
export async function listRuntimeCollections() {
  assertNotFrontendListCollections("listRuntimeCollections");
}

function resolveEngineUsed(data) {
  return String(data?.engine || data?.source || "unknown").trim();
}

function isAdminSdkCloudFunctionResponse(data) {
  const engine = resolveEngineUsed(data);
  return (
    engine === "admin_sdk" ||
    engine === "server" // legacy deployed callable (Admin SDK on default project)
  );
}

/** Surface structured Cloud Function permission errors with actionable guidance. */
export function formatCallableDiscoveryError(err) {
  const details = err?.details;
  if (details && typeof details === "object") {
    const parts = [];
    if (details.message) parts.push(String(details.message));
    if (Array.isArray(details.requiredRoles) && details.requiredRoles.length) {
      parts.push(`Required IAM:\n• ${details.requiredRoles.join("\n• ")}`);
    }
    if (details.credentialMode === "application_default_cross_project") {
      parts.push(
        "Fix: Connection Manager → Edit connection → Service Account → upload the customer Firebase service account JSON."
      );
    }
    if (parts.length) return parts.join("\n\n");
  }

  const msg = err?.message || String(err);
  if (/missing or insufficient permissions/i.test(msg)) {
    return (
      "Missing or insufficient permissions on the customer Firebase project. " +
      "The browser is not signed into that project, so discovery must run via Cloud Function + service account. " +
      "Edit the connection and upload the customer service account JSON (Cloud Datastore Viewer role)."
    );
  }
  return msg;
}

/**
 * Map Admin SDK discovery payload to sidebar registry rows.
 */
export function mapDiscoveryToRegistryList(discovery) {
  const entries = Array.isArray(discovery?.registryEntries) ? discovery.registryEntries : [];
  if (entries.length) {
    return entries
      .map((entry) => ({
        id: entry.id || entry.collectionName,
        collectionName: entry.collectionName || entry.id,
        createdAt: entry.createdAt || null,
        createdBy: entry.createdBy || null,
        documentCount: typeof entry.documentCount === "number" ? entry.documentCount : null,
        lastUpdated: entry.lastUpdated || null,
        status: entry.status === "empty" ? "empty" : "active",
      }))
      .sort((a, b) => a.collectionName.localeCompare(b.collectionName));
  }

  const names = Array.isArray(discovery?.collections) ? discovery.collections : [];
  const ts = new Date().toISOString();
  return names
    .map((name) => ({
      id: name,
      collectionName: name,
      createdAt: ts,
      createdBy: null,
      documentCount: null,
      lastUpdated: ts,
      status: "active",
    }))
    .sort((a, b) => a.collectionName.localeCompare(b.collectionName));
}

/**
 * Discover collections via Cloud Function (Admin SDK).
 */
export async function discoverCollectionsViaAdminSdk({
  connectionId = null,
  runtimeProjectId = null,
  syncRegistry = false,
  createdBy = null,
} = {}) {
  const activeConnectionId = connectionId ?? (hasActiveRuntime() ? getActiveRuntimeConnectionId() : null);
  const expectedProjectId =
    runtimeProjectId ?? getRuntimeDb()?.app?.options?.projectId ?? null;

  const payload = {};
  if (activeConnectionId) payload.connectionId = activeConnectionId;
  if (expectedProjectId) payload.runtimeProjectId = expectedProjectId;
  if (syncRegistry) payload.syncRegistry = true;
  if (createdBy) payload.createdBy = createdBy;

  console.info("[collectionDiscovery] invoking Cloud Function", {
    "engine used": DISCOVERY_ENGINE,
    connectionId: activeConnectionId,
    runtimeProjectId: expectedProjectId,
    payload,
  });

  let res;
  try {
    res = await listFirestoreCollections(payload);
  } catch (err) {
    const code = err?.code ? ` [${err.code}]` : "";
    throw new Error(
      `listFirestoreCollections Cloud Function failed${code}: ${formatCallableDiscoveryError(err)}`
    );
  }

  const data = res?.data;
  console.info("[collectionDiscovery] cloud function response", {
    "engine used": resolveEngineUsed(data),
    "cloud function response": data,
  });

  if (!data || typeof data !== "object") {
    throw new Error("Cloud Function returned no discovery payload");
  }

  if (!isAdminSdkCloudFunctionResponse(data)) {
    throw new Error(
      `Collection discovery did not run via Admin SDK Cloud Function (engine=${resolveEngineUsed(data)})`
    );
  }

  const engineUsed = resolveEngineUsed(data);
  const isModern = engineUsed === "admin_sdk" && Boolean(data.version);

  if (hasActiveRuntime()) {
    if (!isModern || !isVersionAtLeast(data.version, COLLECTION_DISCOVERY_MIN_VERSION)) {
      throw new Error(
        `Customer project discovery requires listFirestoreCollections v${COLLECTION_DISCOVERY_MIN_VERSION}+ with connectionId support. ` +
          `Deploy: firebase deploy --only functions:listFirestoreCollections. ` +
          `Received engine=${engineUsed}, version=${data.version || "none"}.`
      );
    }
    if (data.projectId && expectedProjectId && data.projectId !== expectedProjectId) {
      throw new Error(
        `Discovery project mismatch: Cloud Function listed project "${data.projectId}" but active runtime is "${expectedProjectId}".`
      );
    }
    if (!data.connectionId && activeConnectionId) {
      throw new Error(
        "Cloud Function did not receive connectionId — deploy listFirestoreCollections v2.1.0+ for customer discovery."
      );
    }
  }

  if (!Array.isArray(data.collections)) {
    throw new Error("Cloud Function returned invalid collections array");
  }

  const collections = data.collections.map(normalizeName).filter(Boolean);

  console.info("[collectionDiscovery] collections returned", {
    "engine used": engineUsed,
    "collections returned": collections,
    "collections returned count": collections.length,
    version: data.version ?? null,
    projectId: data.projectId ?? expectedProjectId,
    connectionId: data.connectionId ?? activeConnectionId,
  });

  return {
    collections,
    version: data.version ?? null,
    projectId: data.projectId ?? expectedProjectId,
    connectionId: data.connectionId ?? activeConnectionId,
    source: data.source || engineUsed,
    engine: engineUsed,
    cloudFunctionResponse: data,
    registrySynced: Boolean(data.registrySynced),
    registryWritten: typeof data.registryWritten === "number" ? data.registryWritten : 0,
    registryEntries: Array.isArray(data.registryEntries) ? data.registryEntries : [],
  };
}

/**
 * Verify that the collection is readable in the connected runtime by reading
 * at least one document.
 */
export async function verifyRuntimeCollectionExists(
  collectionName,
  firestore = getRuntimeDb()
) {
  const name = normalizeName(collectionName);
  if (!name) throw new Error("Collection name required");

  const snap = await getDocs(query(collection(firestore, name), limit(1)));
  if (snap.empty) return { exists: true, documentCount: 0 };
  return { exists: true, documentCount: null };
}
